import "./TreeNavigation.scss";

import { NOT_AVAILABLE } from "components/Header/InfoPill/InfoPillTypes";
import NavLink from "components/NavLink/NavLink.tsx";
import { SECTION } from "constants/SettingsConstants";
import type { Location } from "history";
import { groupBy } from "lodash";
import { useLienFileData } from "pages/liens/hooks/useLienFileData";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useGetOrganizationSettingsQuery } from "services/misc-api";
import type { UserType } from "types/DataTypes";
import { useSelector } from "utils/redux";

import { useRecentlyViewedFiles } from "./useRecentlyViewedFiles";

type TreeNavigationProps = {
  data: TreeNavigationItem[];
};

export type TreeNavigationItem = {
  id?: string;
  name: string;
  to: string;
  children?: TreeNavigationItem[];
  isHidden?: (props: {
    loggedInUser: UserType;
    location: Location;
    featureFlags: Record<string, boolean>;
  }) => boolean;
  isSeparated?: boolean;
};

type RouteParams = {
  fileId: string;
  claimId: string;
  rreId: string;
  msaId: string;
  clientId: string;
  claimantId: string;
  employerId: string;
  insurerId: string;
  tpaId: string;
  miscServiceId: string;
  reviewId: string;
  mcpId: string;
  lcpId: string;
  costProjectionId: string;
  lienId: string;
  documentManagementFileId: string;
};

type SlugMap = {
  [key: string]: string;
};

enum NodeType {
  PART = "part",
  DOI = "doi",
  CASE = "case",
}

type LienNavigationNode = {
  id: string;
  name: string;
  to: string;
  children?: LienNavigationNode[];
};

type RecentlyViewedNavigationNode = {
  id: string;
  name: string;
  to: string;
  children?: RecentlyViewedNavigationNode[];
};

const TreeNode = ({
  item,
  level,
}: {
  item: TreeNavigationItem;
  level: number;
}) => {
  const location = useLocation();
  const params = useParams<RouteParams>();
  const loggedInUser = useSelector((state) => state.main.loggedInUser);
  const constants = useSelector((state) => state.shared.constants);
  const { data: organizationSettings } = useGetOrganizationSettingsQuery();

  // grab file id from lien file, since lien file is is always in the url
  const { lienFileData, isLoading: isLienFileDataLoading } = useLienFileData();

  // grab recently viewed files
  const { recentlyViewedFiles, isLoading: isRecentlyViewedFilesLoading } =
    useRecentlyViewedFiles();

  const liens = useSelector((state) => state.liens.liens);
  const lienfileIdMatch = location.pathname.match(/\/liens\/(\d+)/);
  const lienfileId = lienfileIdMatch ? lienfileIdMatch[1] : undefined;

  const isLiensSection = location.pathname.includes("/liens");
  const isLiensNode = item.id === SECTION.LIENS;
  const isRecentlyViewedNode = item.id === "o0-recently-viewed";
  const isOperationsSection = location.pathname.includes("/operations");

  const getNodeType = (id?: string): NodeType | null => {
    if (!id) return null;
    if (id.startsWith(`${NodeType.PART}-`)) return NodeType.PART;
    if (id.startsWith(`${NodeType.DOI}-`)) return NodeType.DOI;
    if (id.startsWith(`${NodeType.CASE}-`)) return NodeType.CASE;
    return null;
  };

  // determine if node should be expanded
  const shouldExpandLiensChildren = useMemo(() => {
    if (!isLiensSection) return false;
    if (isLiensNode) return true;

    const nodeType = getNodeType(item.id);
    return nodeType === NodeType.PART || nodeType === NodeType.DOI;
  }, [isLiensSection, isLiensNode, item.id]);

  // determine if recent files should be expanded
  const shouldExpandRecentFiles = useMemo(() => {
    return isOperationsSection && isRecentlyViewedNode;
  }, [isOperationsSection, isRecentlyViewedNode]);

  // lien navigation structure
  const createLienNavigation = useCallback((): LienNavigationNode[] => {
    const directoryNavItem: LienNavigationNode = {
      id: "directory",
      name: "Directory",
      to: `/liens/${lienfileId}/directory`,
    };

    const queryNavItem: LienNavigationNode = {
      id: "query",
      name: "Query",
      to: `/liens/${lienfileId}/query`,
    };

    const invoiceNavItem: LienNavigationNode = {
      id: "invoice",
      name: "Invoice",
      to: `/liens/${lienfileId}/invoice?file_id=${lienFileData?.file_id}`,
    };

    const notesNavItem: LienNavigationNode = {
      id: "notes",
      name: "Notes",
      to: `/liens/${lienfileId}/notes`,
    };

    const baseNavItems = [directoryNavItem, queryNavItem];
    if (organizationSettings?.quickbooks_integration_enabled) {
      baseNavItems.push(invoiceNavItem);
    }
    baseNavItems.push(notesNavItem);

    // if no liens exist, return base nav items Directory, Query, and Invoice (if enabled)
    if (!liens?.length) {
      return baseNavItems;
    }

    const liensByType = groupBy(liens, "part_type");
    const partTypeOrder = ["AB", "C", "D"];

    return [
      ...baseNavItems,
      ...Object.entries(liensByType)
        .sort(([a], [b]) => {
          return partTypeOrder.indexOf(a) - partTypeOrder.indexOf(b);
        })
        .map(([partType, typeLines]) => {
          const liensByDOI = groupBy(typeLines, "date_of_injury");

          return {
            id: `${NodeType.PART}-${partType}`,
            name: partType === "AB" ? "A/B" : partType,
            to: "#",
            // for each part type, group by date of injury
            children: Object.entries(liensByDOI).map(([doi, doiLiens]) => ({
              id: `${NodeType.DOI}-${doi}`,
              name: `DOI ${doi && doi !== "null" ? doi : NOT_AVAILABLE}`,
              to: "#",
              // for each date of injury, map by case number or part number
              children: doiLiens.map((lien, index) => ({
                id: `${NodeType.CASE}-${lien.id}`,
                name:
                  // for Part A/B, show case number, for Part C/D, show plan number - case number
                  partType === "AB"
                    ? `Case ID- ${lien.case_number}`
                    : `Plan ${index + 1} - ${lien.case_number}`,
                to: lien.workspace_url,
                children: [
                  {
                    id: `${NodeType.CASE}-${lien.id}-audit-history`,
                    name: "Audit History",
                    to: `/liens/${lienfileId}/lien/${lien.id}/audit-history`,
                  },
                ],
              })),
            })),
          };
        }),
    ];
  }, [
    liens,
    lienfileId,
    lienFileData?.file_id,
    isLienFileDataLoading,
    organizationSettings,
  ]);

  // recently viewed files navigation structure
  const createRecentlyViewedNavigation =
    useCallback((): RecentlyViewedNavigationNode[] => {
      if (!recentlyViewedFiles?.length) {
        return [];
      }

      return recentlyViewedFiles.map((file) => ({
        id: `recently-viewed-file-${file.id}`,
        name: `#${file.id} - ${file.claimant_name}`,
        to: `/operations/files/${file.id}/edit/claimant-information`,
      }));
    }, [recentlyViewedFiles]);

  /*
  maps route parameter slugs to actual values from url params
  when adding new routes with params, both RouteParams type and this mapping must be updated
  */
  const getSlugToRecordMap = (params: Partial<RouteParams>): SlugMap => ({
    ":fileId": params.fileId || "",
    ":claimId": params.claimId || "",
    ":rreId": params.rreId || "",
    ":msaId": params.msaId || "",
    ":lcpId": params.lcpId || params.costProjectionId || "",
    ":mcpId": params.mcpId || params.costProjectionId || "",
    ":reviewId": params.reviewId || "",
    ":clientId": params.clientId || "",
    ":claimantId": params.claimantId || "",
    ":employerId": params.employerId || "",
    ":insurerId": params.insurerId || "",
    ":tpaId": params.tpaId || "",
    ":miscServiceId": params.miscServiceId || "",
    ":lienId": params.lienId || "",
    ":documentManagementFileId": params.documentManagementFileId || "",
  });

  const getChildRecordSlugs = (children?: TreeNavigationItem[]): string[] => {
    if (!children) return [];

    return children
      .map((child) => {
        const slugMatch = child.to.match(/:id\b|:[a-zA-Z]*Id\b/);
        return slugMatch ? slugMatch[0] : null;
      })
      .filter((s): s is string => s !== null);
  };

  const buildAccuratePath = (path: string, slugMap: SlugMap): string => {
    return path
      .split("/")
      .map((segment) => (segment.startsWith(":") ? slugMap[segment] : segment))
      .join("/");
  };

  const slugToRecordMap = getSlugToRecordMap(params);
  const childRecordSlugs = getChildRecordSlugs(item.children);
  const hasDetailedRoutes = childRecordSlugs.length > 0;
  const recordId = hasDetailedRoutes
    ? slugToRecordMap[childRecordSlugs[0]]
    : "";

  // null check prevents errors when new route params are added without updating slug mapping above
  const shouldExpandChildren =
    !hasDetailedRoutes ||
    (hasDetailedRoutes && recordId && recordId.length > 0);

  const accurateTo = buildAccuratePath(item.to, slugToRecordMap);

  // future medical specific logic
  const isFutureMedicalOverview =
    location.pathname === "/future-medical/overview";
  const currentFutureMedicalSection = location.pathname.split("/")[2]; // gets 'msa', 'lcp', etc.

  const shouldHideChildren =
    (item.id === SECTION.FUTURE_MEDICAL && isFutureMedicalOverview) ||
    (level === 2 &&
      item.to.startsWith("/future-medical/") &&
      currentFutureMedicalSection &&
      !item.to.includes(`/future-medical/${currentFutureMedicalSection}`));

  const shouldShowChild = (child: TreeNavigationItem) => {
    if (
      level === 1 &&
      item.id === SECTION.FUTURE_MEDICAL &&
      currentFutureMedicalSection
    ) {
      return child.to.includes(
        `/future-medical/${currentFutureMedicalSection}`,
      );
    }

    return child?.isHidden
      ? !child.isHidden({
          loggedInUser,
          location,
          featureFlags: constants?.FEATURE_FLAGS,
        })
      : true;
  };

  const isNodeActive =
    location.pathname === accurateTo ||
    location.pathname.startsWith(`${accurateTo}/`) ||
    (item.id === SECTION.FUTURE_MEDICAL &&
      location.pathname.includes("/future-medical")) ||
    (item.id === SECTION.LIENS && location.pathname.includes("/liens")) ||
    (location.pathname.includes("overview") &&
      item.to.includes("overview") &&
      location.pathname.startsWith(accurateTo.split("?")[0])) ||
    (item.id === "invoice" &&
      location.pathname.includes("/invoice") &&
      location.pathname.includes("/liens/")) ||
    (item.id === "directory" &&
      location.pathname.includes("/directory") &&
      location.pathname.includes("/liens/")) ||
    (item.id === "query" &&
      location.pathname.includes("/query") &&
      location.pathname.includes("/liens/")) ||
    (item.id === "notes" &&
      location.pathname.includes("/notes") &&
      location.pathname.includes("/liens/")) ||
    (item.id === SECTION.DOCUMENT_MANAGEMENT &&
      location.pathname.includes("/document-management")) ||
    (item.id === "o0-recently-viewed" &&
      location.pathname === "/operations/recently-viewed");

  const [isExpanded, setExpanded] = useState(
    isNodeActive || shouldExpandLiensChildren || shouldExpandRecentFiles,
  );

  useEffect(() => {
    if (level === 1) {
      if (isNodeActive) {
        setExpanded(true);
      }
    } else if (level === 2) {
      if (
        (params.fileId ||
          params.claimId ||
          params.rreId ||
          params.msaId ||
          params.reviewId ||
          params.mcpId ||
          params.lcpId ||
          params.lienId) &&
        (isNodeActive || shouldExpandLiensChildren)
      ) {
        setExpanded(true);
      }
      // auto-expand recent files when in operations section
      if (shouldExpandRecentFiles) {
        setExpanded(true);
      }
    } else if (shouldExpandLiensChildren) {
      setExpanded(true);
    }
  }, [
    isNodeActive,
    setExpanded,
    level,
    params.fileId,
    params.claimId,
    params.rreId,
    params.msaId,
    params.reviewId,
    params.mcpId,
    params.lcpId,
    params.costProjectionId,
    params.lienId,
    shouldExpandLiensChildren,
    shouldExpandRecentFiles,
  ]);

  const children = useMemo(() => {
    if (isLiensSection && isLiensNode) {
      return createLienNavigation();
    }
    if (isRecentlyViewedNode) {
      return createRecentlyViewedNavigation();
    }
    return item.children;
  }, [
    isLiensSection,
    isLiensNode,
    isRecentlyViewedNode,
    createLienNavigation,
    createRecentlyViewedNavigation,
    item.children,
    isLienFileDataLoading,
    isRecentlyViewedFilesLoading,
  ]);

  if (isLienFileDataLoading || isRecentlyViewedFilesLoading) return null;

  return (
    <>
      {/* render separator if this item should be separated from others above it */}
      {item.isSeparated && <div className="nav-separator" />}

      {/* if to is #, render a span instead of a NavLink */}
      {item.to === "#" ? (
        <span
          data-level={level.toString()}
          key={item.id}
          className={`navlink non-clickable-node ${isExpanded ? "expanded" : ""}`}
          onClick={() => setExpanded(!isExpanded)}
        >
          {item.name}
        </span>
      ) : (
        <NavLink
          data-level={level.toString()}
          key={item.id}
          to={accurateTo}
          className={isNodeActive ? "active" : ""}
        >
          {item.name}
        </NavLink>
      )}
      {(isExpanded || shouldExpandLiensChildren || shouldExpandRecentFiles) &&
        children &&
        (shouldExpandChildren ||
          shouldExpandLiensChildren ||
          shouldExpandRecentFiles) &&
        !shouldHideChildren &&
        children
          .filter(shouldShowChild)
          .map((child) => (
            <TreeNode key={child.id} item={child} level={level + 1} />
          ))}
    </>
  );
};

const TreeNavigation = ({ data }: TreeNavigationProps) => (
  <nav className="tree-navigation">
    {data.map((item, index) => (
      <TreeNode key={index} item={item} level={1} />
    ))}
  </nav>
);

TreeNavigation.displayName = "TreeNavigation";

export default TreeNavigation;
